import { cs } from '../../../lib/currency';
/**
 * Enrollment History dialog (iter268 — extracted from Enrollments.js).
 *
 * Pure viewer — renders the timeline for `selectedEnrollment` using the
 * `historyData` array provided by the parent. State (loading, data) is
 * managed in the parent because the fetch is triggered from the
 * "View History" row action which also sets `selectedEnrollment`.
 *
 * Props:
 *   - open               (bool)            — dialog visibility
 *   - onOpenChange       ((bool) => void)  — Radix open handler
 *   - selectedEnrollment (object | null)   — header context (player, program, status)
 *   - historyLoading     (bool)            — spinner while fetching
 *   - historyData        (Array<HistoryEntry>) — timeline rows
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { History, Clock, ArrowRight, User } from 'lucide-react';

const formatActionName = (action) => {
  const actionMap = {
    approved: 'Approved',
    rejected: 'Rejected',
    paused: 'Paused',
    resumed: 'Resumed',
    auto_resumed: 'Auto-Resumed',
    canceled: 'Canceled',
    edited: 'Edited',
    pause_updated: 'Pause Updated',
    admin_moved: 'Moved',
    payment_received: 'Payment Received',
  };
  return actionMap[action] || action;
};

const getActionColor = (action) => {
  const colorMap = {
    approved: 'bg-green-500',
    rejected: 'bg-red-500',
    paused: 'bg-amber-500',
    resumed: 'bg-blue-500',
    auto_resumed: 'bg-green-500',
    canceled: 'bg-red-500',
    edited: 'bg-purple-500',
    pause_updated: 'bg-amber-500',
    admin_moved: 'bg-indigo-500',
    payment_received: 'bg-green-500',
  };
  return colorMap[action] || 'bg-slate-500';
};

export default function EnrollmentHistoryDialog({
  open,
  onOpenChange,
  selectedEnrollment,
  historyLoading,
  historyData,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <History className="h-5 w-5 text-blue-500" />
            Enrollment History
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          {/* Enrollment Info */}
          <div className="p-3 bg-slate-50 rounded-lg mb-4">
            <p className="font-medium text-slate-900">{selectedEnrollment?.player_name}</p>
            <p className="text-sm text-slate-500">Program: {selectedEnrollment?.program_name}</p>
            <p className="text-sm text-slate-500">
              Current Status: <span className="font-medium capitalize">{selectedEnrollment?.status}</span>
            </p>
          </div>

          {/* Timeline */}
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No history available for this enrollment</p>
              <p className="text-xs text-slate-400 mt-1">History tracking started recently</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-slate-200"></div>

              {/* Timeline items */}
              <div className="space-y-4">
                {historyData.map((entry, index) => (
                  <div key={entry.id} className="relative flex gap-4 pl-8" data-testid={`history-entry-${index}`}>
                    {/* Timeline dot */}
                    <div className={`absolute left-1 top-1.5 w-4 h-4 rounded-full ${getActionColor(entry.action)} flex items-center justify-center`}>
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${
                          entry.action === 'approved' ? 'text-green-600' :
                          entry.action === 'rejected' || entry.action === 'canceled' ? 'text-red-600' :
                          entry.action === 'paused' ? 'text-amber-600' :
                          entry.action === 'resumed' ? 'text-blue-600' :
                          'text-purple-600'
                        }`}>
                          {formatActionName(entry.action)}
                        </span>
                        <span className="text-xs text-slate-400">
                          {new Date(entry.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>

                      {/* Status change */}
                      {entry.old_status !== entry.new_status && (
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                          <span className="capitalize">{entry.old_status}</span>
                          <ArrowRight className="h-3 w-3" />
                          <span className="capitalize font-medium">{entry.new_status}</span>
                        </div>
                      )}

                      {/* Admin who made the change */}
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                        <User className="h-3 w-3" />
                        <span>by {entry.admin_name}</span>
                      </div>

                      {/* Notes */}
                      {entry.notes && (
                        <p className="text-sm text-slate-600 mt-2 pt-2 border-t border-slate-100">
                          "{entry.notes}"
                        </p>
                      )}

                      {/* Additional details */}
                      {entry.details && Object.keys(entry.details).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-100">
                          {entry.details.pause_date && (
                            <p className="text-xs text-slate-500">Pause Date: {entry.details.pause_date}</p>
                          )}
                          {entry.details.auto_resume_date && (
                            <p className="text-xs text-green-600">Auto-Resume Date: {entry.details.auto_resume_date}</p>
                          )}
                          {entry.details.cancel_date && (
                            <p className="text-xs text-slate-500">Cancel Date: {entry.details.cancel_date}</p>
                          )}
                          {entry.details.resume_date && (
                            <p className="text-xs text-slate-500">Resume Date: {entry.details.resume_date}</p>
                          )}
                          {entry.details.start_date && (
                            <p className="text-xs text-slate-500">Start Date: {entry.details.start_date}</p>
                          )}
                          {entry.details.monthly_fee && (
                            <p className="text-xs text-slate-500">Monthly Fee: {cs()}{entry.details.monthly_fee}</p>
                          )}
                          {/* Payment details */}
                          {entry.details.payment_method && (
                            <div className="text-xs space-y-1">
                              <p className="text-slate-500">
                                <span className="font-medium">Method:</span> {entry.details.payment_method === 'other'
                                  ? entry.details.payment_method_other || 'Other'
                                  : entry.details.payment_method.charAt(0).toUpperCase() + entry.details.payment_method.slice(1)}
                              </p>
                              {entry.details.payment_type && (
                                <p className="text-slate-500">
                                  <span className="font-medium">Type:</span> {entry.details.payment_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </p>
                              )}
                              {entry.details.amount && (
                                <p className="text-green-600 font-medium">
                                  Amount: {cs()}{entry.details.amount.toFixed ? entry.details.amount.toFixed(2) : entry.details.amount}
                                </p>
                              )}
                            </div>
                          )}
                          {/* Movement details */}
                          {entry.details.from_program_name && entry.details.to_program_name && (
                            <div className="text-xs space-y-1">
                              <p className="text-slate-500">
                                Program: <span className="font-medium">{entry.details.from_program_name}</span> → <span className="font-medium text-blue-600">{entry.details.to_program_name}</span>
                              </p>
                              {entry.details.from_class_name && entry.details.to_class_name && (
                                <p className="text-slate-500">
                                  Class: <span className="font-medium">{entry.details.from_class_name}</span> → <span className="font-medium text-blue-600">{entry.details.to_class_name}</span>
                                </p>
                              )}
                              {entry.details.old_fee !== undefined && entry.details.new_fee !== undefined && entry.details.old_fee !== entry.details.new_fee && (
                                <p className="text-amber-600">
                                  Fee: {cs()}{entry.details.old_fee} → {cs()}{entry.details.new_fee}
                                </p>
                              )}
                              {entry.details.effective_date && (
                                <p className="text-slate-500">Effective Date: {entry.details.effective_date}</p>
                              )}
                              {entry.details.reason && (
                                <p className="text-slate-500 mt-1">Reason: {entry.details.reason}</p>
                              )}
                            </div>
                          )}
                          {entry.details.changes && (
                            <div className="text-xs text-slate-500">
                              {entry.details.changes.monthly_fee && (
                                <p>Fee: {cs()}{entry.details.changes.monthly_fee.old || 'N/A'} → {cs()}{entry.details.changes.monthly_fee.new}</p>
                              )}
                              {entry.details.changes.payment_frequency && (
                                <p>Frequency: {entry.details.changes.payment_frequency.old || 'N/A'} → {entry.details.changes.payment_frequency.new}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
